import React, {useState, ReactNode, useEffect} from 'react';
import {
    DataGridPro,
    GridColDef,
    GridActionsCellItem,
    GridRowModesModel,
    GridRowModes,
    GridRowId,
    GridRowModel,
    GridToolbarContainer,
} from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { Button, Box, Typography } from '@mui/material';
import {ColumnDef} from "@/utils/tablesUtils";
import {EditorialAdditionalInfo, FormAdditionalInfo, HeaderDef} from "@/types/editorialAssetsTypes";

export type FlatRow = Record<string, any> & { id: GridRowId; isNew?: boolean };

export interface DynamicTableProps {
    columns: ColumnDef[];
    header: EditorialAdditionalInfo;
    rows: FlatRow[];
    logo?: ReactNode | string;
    actionsWidth?: number; // NOVITÀ: Larghezza per la colonna Azioni
    onAddRow: (row: FlatRow) => void;
    onEditRow: (id: GridRowId, row: FlatRow) => void;
    onDeleteRow: (id: GridRowId) => void;
}

// --- COMPONENTE ---

export const DynamicDataGridPro: React.FC<DynamicTableProps> = ({
                                                                    columns = [],
                                                                    header,
                                                                    rows = [],
                                                                    logo,
                                                                    actionsWidth = 70, // Valore di default super compatto per le 2 icone
                                                                    onAddRow,
                                                                    onEditRow,
                                                                    onDeleteRow,
                                                                }) => {
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

    // 1. Configurazione Colonne con Larghezze
    const gridColumns: GridColDef[] = columns.map((c) => {
        // SE LA COLONNA È MULTISTRING (Dropdown)
        if (c.type === 'multistring') {
            return {
                field: c.name,
                headerName: c.label,
                type: 'singleSelect', // <-- Il tipo magico di MUI per le select
                // MUI DataGrid vuole 'value' e 'label', quindi mappiamo il tuo 'name' in 'value'
                valueOptions: c.options?.map((opt) => ({
                    value: opt.name,
                    label: opt.label,
                })) || [],
                width: c.width,
                flex: c.width ? undefined : (c.flex || 1),
                editable: true,
            };
        }

        // SE È UNA COLONNA NORMALE (Testo o Numero)
        return {
            field: c.name,
            headerName: c.label,
            type: c.type === 'string' ? 'string' : 'number',
            width: c.width,
            flex: c.width ? undefined : (c.flex || 1),
            editable: true,
        };
    });

    // Aggiunta della colonna Azioni
    gridColumns.push({
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: actionsWidth, // Usa la prop passata dall'esterno
        cellClassName: 'actions',
        getActions: ({ id }) => {
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

            if (isInEditMode) {
                return [
                    <GridActionsCellItem
                        icon={<SaveIcon fontSize="small" />} // Icone rimpicciolite
                        label="Save"
                        sx={{ color: 'primary.main' }}
                        onClick={handleSaveClick(id)}
                    />,
                    <GridActionsCellItem
                        icon={<CancelIcon fontSize="small" />}
                        label="Cancel"
                        className="textPrimary"
                        onClick={handleCancelClick(id)}
                        color="inherit"
                    />,
                ];
            }

            return [
                <GridActionsCellItem
                    icon={<EditIcon fontSize="small" />}
                    label="Edit"
                    className="textPrimary"
                    onClick={handleEditClick(id)}
                    color="inherit"
                />,
                <GridActionsCellItem
                    icon={<DeleteIcon fontSize="small" />}
                    label="Delete"
                    onClick={handleDeleteClick(id)}
                    color="inherit"
                />,
            ];
        },
    });

    // --- HANDLERS (Identici a prima) ---
    const handleEditClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id: GridRowId) => () => {
        onDeleteRow(id);
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });
        const editedRow = rows.find((row) => row.id === id);
        if (editedRow?.isNew) {
            onDeleteRow(id);
        }
    };

    const processRowUpdate = (newRow: GridRowModel) => {
        const updatedRow = { ...newRow, isNew: false } as FlatRow;
        if (newRow.isNew) {
            onAddRow(updatedRow);
        } else {
            onEditRow(newRow.id, updatedRow);
        }
        return updatedRow;
    };

    // --- TOOLBAR ---
    const CustomToolbar = () => {
        const handleAddClick = () => {
            const id = `new-${Date.now()}`;
            const emptyRow = columns.reduce(
                (acc, col) => ({ ...acc, [col.name]: '' }),
                { id, isNew: true }
            ) as FlatRow;
            onAddRow(emptyRow);
            setRowModesModel((oldModel) => ({
                ...oldModel,
                [id]: { mode: GridRowModes.Edit, fieldToFocus: columns[0]?.name },
            }));
        };

        const renderLogo = () => {
            if (!logo) return null;
            if (typeof logo === 'string') return <img src={header.logo} alt="logo" style={{ maxHeight: '20px', margin: '0 4px' }} />;
            return <Box sx={{ mx: 0.5, display: 'flex', alignItems: 'center' }}>{header.logo}</Box>;
        };

        return (
            <GridToolbarContainer sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, minHeight: '36px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {renderLogo()}
                    <Typography variant="body1" component="div" fontWeight="bold">
                        {header.label}
                    </Typography>
                </Box>
                <Button size="small" color="primary" startIcon={<AddIcon fontSize="small" />} onClick={handleAddClick}>
                    Add
                </Button>
            </GridToolbarContainer>
        );
    };

    return (
        // Rimosso l'altezza fissa (height: 500), usiamo flex o auto per adattarci al contenitore
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <DataGridPro
                rows={rows}
                columns={gridColumns}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
                processRowUpdate={processRowUpdate}
                density="compact" // <-- CRUCIALE: Mette la tabella in modalità compatta nativa
                rowHeight={36} // <-- Forza le righe ad essere sottilissime (default compatto è 43)
                columnHeaderHeight={40} // <-- Header super sottile
                hideFooter // <-- Opzionale: toglie la paginazione in basso per salvare altro spazio (rimuovilo se ti serve!)
                slots={{
                    toolbar: CustomToolbar,
                }}
                sx={{
                    // 1. Uccidiamo gli spazi della cella
                    '& .MuiDataGrid-cell.actions': {
                        gap: '0px !important',
                        padding: '0px !important',
                        display: 'flex',
                        justifyContent: 'center',
                    },
                    // 2. Riduciamo all'osso il bottone cliccabile
                    '& .MuiDataGrid-cell.actions .MuiIconButton-root': {
                        padding: '2px !important',
                        margin: '0px !important',
                        minWidth: 'unset !important',
                        width: '22px !important',  // <-- Forza la larghezza del bottone
                        height: '22px !important', // <-- Forza l'altezza del bottone
                    },
                    // 3. Rimpiccioliamo direttamente il disegno dell'icona (l'elemento SVG)
                    '& .MuiDataGrid-cell.actions .MuiIconButton-root svg': {
                        fontSize: '16px !important', // <-- Scendi a 14px o 12px se le vuoi microscopiche
                    },
                    // 4. Via i bordi di focus
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none !important',
                    }
                }}
            />
        </Box>
    );
};

export default DynamicDataGridPro;